import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  InboxItem,
  GetInboxItemsResponse,
  GetInboxItemsParams,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from '../contexts/PusherContext';
import api from '../api';

type InboxErrorType = 'NETWORK_ERROR' | 'UNKNOWN_ERROR' | 'KNOWN_ERROR';

interface InboxError {
  type: InboxErrorType;
  message: string;
}

interface InboxState {
  notYetValidCount: number;
  items: InboxItem[];
  loading: boolean;
  error: InboxError | null;
  lastUpdated: number;
}

interface InboxContextType {
  getInbox: (agentId?: string) => InboxState;
  refreshInbox: (agentId?: string) => Promise<void>;
  dismissItem: (itemId: string) => Promise<void>;
  markAsRead: (itemId: string) => Promise<void>;
  changeUrgency: (itemId: string, isUrgent: boolean) => Promise<void>;
  isLoading: (agentId?: string) => boolean;
}

const InboxContext = createContext<InboxContextType | null>(null);

interface InboxProviderProps {
  children: React.ReactNode;
}

export const InboxProvider: React.FC<InboxProviderProps> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const [inboxes, setInboxes] = useState<Map<string, InboxState>>(new Map());
  const refreshTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const getDefaultState = (): InboxState => ({
    notYetValidCount: 0,
    items: [],
    loading: true,
    error: null,
    lastUpdated: 0,
  });

  const getInboxKey = (agentId?: string): string => {
    return agentId || 'all';
  };

  const fetchInbox = useCallback(
    async (agentId?: string): Promise<InboxState> => {
      try {
        const accessToken = await getAccessTokenSilently();
        const params: GetInboxItemsParams = agentId
          ? { agent_id: agentId }
          : {};

        const response = await api.get('/inbox', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params,
        });

        const data: GetInboxItemsResponse = response.data;

        if (data.success) {
          return {
            items: data.items,
            notYetValidCount: data.notYetValidCount,
            loading: false,
            error: null,
            lastUpdated: Date.now(),
          };
        } else {
          // Backend returned success: false with an error message
          return {
            items: [],
            notYetValidCount: 0,
            loading: false,
            error: {
              type: 'KNOWN_ERROR',
              message: data.error || 'Failed to load inbox',
            },
            lastUpdated: Date.now(),
          };
        }
      } catch (error: any) {
        console.error('Error fetching inbox:', error);

        let errorInfo: InboxError;

        if (!error.response) {
          // Network connectivity issues - no response received from server
          errorInfo = {
            type: 'NETWORK_ERROR',
            message:
              'Unable to connect to the server. Please check your internet connection.',
          };
        } else if (error.response?.status >= 500) {
          // Server errors (5xx)
          errorInfo = {
            type: 'UNKNOWN_ERROR',
            message: 'An unknown error occurred. Please try again later.',
          };
        } else if (error.response?.status >= 400) {
          // Client errors (4xx) - treat as known errors with backend message if available
          errorInfo = {
            type: 'KNOWN_ERROR',
            message:
              error.response?.data?.error ||
              `Request failed with status ${error.response.status}`,
          };
        } else {
          // Any other unexpected errors
          errorInfo = {
            type: 'UNKNOWN_ERROR',
            message: 'An unknown error occurred. Please try again.',
          };
        }

        return {
          items: [],
          notYetValidCount: 0,
          loading: false,
          error: errorInfo,
          lastUpdated: Date.now(),
        };
      }
    },
    [getAccessTokenSilently],
  );

  const refreshInbox = useCallback(
    async (agentId?: string) => {
      const key = getInboxKey(agentId);

      setInboxes((prev) => {
        const current = prev.get(key) || getDefaultState();
        const updated = new Map(prev);
        updated.set(key, {
          ...current,
          loading: true,
          error: null,
        });
        return updated;
      });

      const newState = await fetchInbox(agentId);

      setInboxes((prev) => {
        const updated = new Map(prev);
        updated.set(key, newState);
        return updated;
      });
    },
    [fetchInbox],
  );

  const dismissItem = useCallback(
    async (itemId: string) => {
      // Optimistically set dismissedAt before the API call
      const currentTime = new Date().toISOString();
      setInboxes((prev) => {
        const updated = new Map(prev);
        for (const [key, state] of Array.from(prev)) {
          updated.set(key, {
            ...state,
            items: state.items.map((item: InboxItem) =>
              item.id === itemId ? { ...item, dismissedAt: currentTime } : item,
            ),
            lastUpdated: Date.now(),
          });
        }
        return updated;
      });

      try {
        const accessToken = await getAccessTokenSilently();

        await api.post(
          '/inbox/dismiss',
          { itemId },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );
      } catch (error) {
        console.error('Error dismissing inbox item:', error);
        // Revert the optimistic update on error
        setInboxes((prev) => {
          const updated = new Map(prev);
          for (const [key, state] of Array.from(prev)) {
            updated.set(key, {
              ...state,
              items: state.items.map((item: InboxItem) =>
                item.id === itemId ? { ...item, dismissedAt: null } : item,
              ),
              lastUpdated: Date.now(),
            });
          }
          return updated;
        });
      }
    },
    [getAccessTokenSilently],
  );

  const markAsRead = useCallback(
    async (itemId: string) => {
      try {
        const accessToken = await getAccessTokenSilently();

        await api.post(
          '/inbox/mark-read',
          { itemId },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        // Optimistically update the item in all inboxes
        setInboxes((prev) => {
          const updated = new Map(prev);
          for (const [key, state] of Array.from(prev)) {
            updated.set(key, {
              ...state,
              items: state.items.map((item: InboxItem) =>
                item.id === itemId ? { ...item, isRead: true } : item,
              ),
              lastUpdated: Date.now(),
            });
          }
          return updated;
        });
      } catch (error) {
        console.error('Error marking inbox item as read:', error);
        // Refresh all inboxes on error
        for (const key of Array.from(inboxes.keys())) {
          const agentId = key === 'all' ? undefined : key;
          refreshInbox(agentId);
        }
      }
    },
    [getAccessTokenSilently, inboxes, refreshInbox],
  );

  const changeUrgency = useCallback(
    async (itemId: string, isUrgent: boolean) => {
      try {
        const accessToken = await getAccessTokenSilently();

        // Map isUrgent to category for backward compatibility with API
        const category = isUrgent ? 'URGENT' : 'SHOW_IN_INBOX';

        await api.post(
          '/inbox/change-urgency',
          { itemId, isUrgent },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        // Optimistically update the item in all inboxes
        setInboxes((prev) => {
          const updated = new Map(prev);
          for (const [key, state] of Array.from(prev)) {
            updated.set(key, {
              ...state,
              items: state.items.map((item: InboxItem) =>
                item.id === itemId ? { ...item, isUrgent } : item,
              ),
              lastUpdated: Date.now(),
            });
          }
          return updated;
        });
      } catch (error) {
        console.error('Error changing inbox item urgency:', error);
        // Refresh all inboxes on error
        for (const key of Array.from(inboxes.keys())) {
          const agentId = key === 'all' ? undefined : key;
          refreshInbox(agentId);
        }
      }
    },
    [getAccessTokenSilently, inboxes, refreshInbox],
  );

  // Listen for inbox-updated events on the existing Pusher channel
  useEffect(() => {
    if (!pusherContext?.pusherChannel) {
      console.log('Pusher channel not available yet for inbox updates');
      return;
    }

    const handleInboxUpdate = (data: { agentId: string; userId: string }) => {
      console.log('Received inbox update:', data);

      // Debounce refresh calls to avoid excessive API requests
      const agentId = data.agentId;
      const key = getInboxKey(agentId);

      // Clear any existing timeout for this agent
      const existingTimeout = refreshTimeoutsRef.current.get(key);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set a new timeout to refresh after a short delay
      const timeout = setTimeout(() => {
        refreshInbox(agentId);
        refreshTimeoutsRef.current.delete(key);
      }, 500); // 500ms debounce

      refreshTimeoutsRef.current.set(key, timeout);

      // Also refresh the "all" inbox if this was for a specific agent
      if (agentId) {
        const allKey = getInboxKey();
        const existingAllTimeout = refreshTimeoutsRef.current.get(allKey);
        if (existingAllTimeout) {
          clearTimeout(existingAllTimeout);
        }

        const allTimeout = setTimeout(() => {
          refreshInbox();
          refreshTimeoutsRef.current.delete(allKey);
        }, 500);

        refreshTimeoutsRef.current.set(allKey, allTimeout);
      }
    };

    // Bind to the inbox-updated event on the existing channel
    pusherContext.pusherChannel.bind('inbox-updated', handleInboxUpdate);
    console.log(
      'Listening for inbox-updated events on existing Pusher channel',
    );

    // Cleanup on unmount
    return () => {
      pusherContext.pusherChannel.unbind('inbox-updated', handleInboxUpdate);

      // Clear all timeouts
      for (const timeout of Array.from(refreshTimeoutsRef.current.values())) {
        clearTimeout(timeout);
      }
      refreshTimeoutsRef.current.clear();
    };
  }, [pusherContext, refreshInbox]);

  const getInbox = useCallback(
    (agentId?: string): InboxState => {
      const key = getInboxKey(agentId);
      const existing = inboxes.get(key);

      if (!existing) {
        // Initialize the inbox and start fetching
        const initialState = getDefaultState();
        setInboxes((prev) => {
          const updated = new Map(prev);
          updated.set(key, initialState);
          return updated;
        });

        // Start fetching
        refreshInbox(agentId);

        return initialState;
      }

      return existing;
    },
    [inboxes, refreshInbox],
  );

  const isLoading = useCallback(
    (agentId?: string): boolean => {
      const key = getInboxKey(agentId);
      const state = inboxes.get(key);
      return state?.loading ?? true;
    },
    [inboxes],
  );

  const contextValue: InboxContextType = {
    getInbox,
    refreshInbox,
    dismissItem,
    markAsRead,
    changeUrgency,
    isLoading,
  };

  return (
    <InboxContext.Provider value={contextValue}>
      {children}
    </InboxContext.Provider>
  );
};

export const useInbox = (): InboxContextType => {
  const context = useContext(InboxContext);
  if (!context) {
    throw new Error('useInbox must be used within an InboxProvider');
  }
  return context;
};
